import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Hand, 
  ArrowRight, 
  Stethoscope,
  Calendar
} from 'lucide-react';
import { treatmentAreas } from '@/data/treatmentAreas';

const TreatmentAreasPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-hero py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <Hand className="w-4 h-4" />
            <span className="text-sm font-semibold">Treatment Areas</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Conditions We Treat
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            At Melbourne Hand Center, we specialize in treating a wide range of hand, wrist, and elbow conditions. 
            Our fellowship-trained surgeons provide both surgical and non-surgical treatment options.
          </p>
        </div>
      </section>

      {/* Treatment Areas Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {treatmentAreas.map((treatment, index) => (
              <Card 
                key={treatment.slug}
                className="group bg-card border-0 shadow-card hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              >
                <CardContent className="p-6 lg:p-8 flex flex-col h-full">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                    <Stethoscope className="w-7 h-7 text-primary group-hover:text-primary-foreground transition-colors" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                    {treatment.title}
                  </h3>
                  <p className="text-muted-foreground mb-6 flex-grow">
                    {treatment.shortDescription}
                  </p>
                  <Link 
                    to={`/treatment-areas/${treatment.slug}`}
                    className="inline-flex items-center gap-2 text-primary font-semibold group-hover:gap-3 transition-all"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-primary-foreground mb-4">
            Ready to Get Expert Care?
          </h2>
          <p className="text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
            Don't let hand pain limit your life. Schedule a consultation with our fellowship-trained specialists today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="white" size="xl" asChild>
              <a 
                href="https://app.acuityscheduling.com/schedule.php?owner=13861726"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Schedule Consultation
              </a>
            </Button>
            <Button variant="heroOutline" size="xl" asChild>
              <a href="tel:321-500-4263">
                Call 321.500.4263
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TreatmentAreasPage;
